// Code relevant to loading attribute value from feature directly
import { Cell, Row } from 'react-stately';
import { FormattedMessage } from 'react-intl';
import Link from 'client/components/aria/Link/Link';

function getValueToDisplay (featureValueForAttribute, key) {
    if (key.includes('date')) {
        try {
            const dateTime = new Date(featureValueForAttribute);
            const localeDate = new Intl.DateTimeFormat('en-gb', {
                timeStyle: 'medium',
                dateStyle: 'short'
            }).format(
                dateTime);
            return localeDate.toString();
        } catch (err) {
            return featureValueForAttribute;
        }
    } else {
        return featureValueForAttribute;
    }
}

const getSimpleValue = (attribute, featureProps) => {
    const featureValueForAttribute = featureProps[attribute?.attributeName];
    const suffixField = attribute?.attributeSuffix;
    const suffixToDisplay = suffixField && featureProps[suffixField] ? featureProps[suffixField] : null;
    const valueToDisplay = getValueToDisplay(featureValueForAttribute, attribute?.attributeName);
    const valueWithOptionalSuffix = suffixToDisplay ? String(valueToDisplay) + ' ' + suffixToDisplay : String(valueToDisplay);
    return valueWithOptionalSuffix;
};

// Code relevant to displaying attribute values via components

const getRowFormattedMessage = (detailFormat, contactDetailsType, typeInLabel) => {
    return 'contactDetails.' + (typeInLabel && contactDetailsType ? contactDetailsType.toLocaleLowerCase() + '.' : '') + detailFormat;
};

const ensureUrlProtocol = (url) => {
    if (!url) {
        return url;
    }
    const trimmedUrl = url.trim();
    if (/^https?:\/\//.test(trimmedUrl) || /^tel:|^mailto:/.test(trimmedUrl)) {
        return trimmedUrl;
    }
    return `https://${trimmedUrl}`;
};

export const getContactDetailRow = (departmentName, telephoneNumber, emailAddress, webForm, singleLineAddress, contactDetailsType, typeInLabel = true) => {
    const returnedRows = [];
    if ((departmentName || '').trim().length) {
        returnedRows.push(<Row key={[
            contactDetailsType,
            departmentName
        ].join('-')}>
            <Cell><FormattedMessage id={getRowFormattedMessage('department', contactDetailsType, typeInLabel)}/></Cell>
            <Cell>{departmentName}</Cell>
        </Row>);
    }
    if ((telephoneNumber || '').trim().length) {
        returnedRows.push(<Row key={[
            contactDetailsType,
            departmentName,
            telephoneNumber
        ].join('-')}>
            <Cell><FormattedMessage id={getRowFormattedMessage('telephone', contactDetailsType, typeInLabel)}/></Cell>
            <Cell><Link href={`tel:${telephoneNumber}`}>{telephoneNumber}</Link></Cell>
        </Row>);
    }
    if ((emailAddress || '').trim().length) {
        returnedRows.push(<Row key={[
            contactDetailsType,
            departmentName,
            emailAddress
        ].join('-')}>
            <Cell><FormattedMessage id={getRowFormattedMessage('email', contactDetailsType, typeInLabel)}/></Cell>
            <Cell><Link href={`mailto:${emailAddress}`}>{emailAddress}</Link></Cell>
        </Row>);
    }
    if ((webForm || '').trim().length) {
        const webFormUrl = ensureUrlProtocol(webForm);
        returnedRows.push(<Row key={[
            contactDetailsType,
            departmentName,
            webForm
        ].join('-')}>
            <Cell><FormattedMessage id={getRowFormattedMessage('webForm', contactDetailsType, typeInLabel)}/></Cell>
            <Cell><Link href={webFormUrl} target="_blank" rel="noopener noreferrer">{webForm}</Link></Cell>
        </Row>);
    }
    if ((singleLineAddress || '').trim().length) {
        returnedRows.push(<Row key={[
            contactDetailsType,
            departmentName,
            singleLineAddress
        ].join('-')}>
            <Cell><FormattedMessage id={getRowFormattedMessage('singleLineAddress', contactDetailsType, typeInLabel)}/></Cell>
            <Cell>{singleLineAddress}</Cell>
        </Row>);
    }
    return returnedRows.flatMap(row => row);
};

const getContactDetailRowsFromContact = (contacts, contactDetailsType) => {
    if (!contacts) {
        return;
    }
    if (contacts.length > 1) {
        return <Row key={contactDetailsType}>
            <Cell><FormattedMessage id={'contactDetails.' + contactDetailsType + '.contactDetails'}/></Cell>
            <Cell><FormattedMessage id={'contactDetails.multiple'}/></Cell>
        </Row>;
    }
    const { departmentName, telephoneNumber, emailAddress, webForm, singleLineAddress } = contacts[0];
    return getContactDetailRow(departmentName, telephoneNumber, emailAddress, webForm, singleLineAddress, contactDetailsType, true);
};
// End Block

const getAttributeDisplayTextAndValue = (attribute, featureProps, supplementalDataProps) => {
    let returnValue;
    let rowReturn;
    if (featureProps[attribute?.attributeName] !== undefined && !attribute?.attributeName.startsWith('RowComponent#')) {
        // has a valid direct feature property for it
        returnValue = getSimpleValue(attribute, featureProps);
    } else if (attribute?.attributeName.startsWith('RowComponent#')) {
        // RowComponent means replace one or more rows for use within a table
        switch (attribute?.attributeName) {
            case 'RowComponent#ContactBusiness':
                rowReturn = getContactDetailRowsFromContact(supplementalDataProps?.contacts?.business, 'business');
                break;
            case 'RowComponent#ContactEmergency':
                rowReturn = getContactDetailRowsFromContact(supplementalDataProps?.contacts?.emergency, 'emergency');
                break;
            default: returnValue = attribute?.defaultValue;
        }
    } else {
        returnValue = attribute.defaultValue;
    }
    return returnValue || rowReturn ? { displayName: attribute.displayName, value: returnValue, row: rowReturn } : null;
};

export default getAttributeDisplayTextAndValue;
